import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #overlayObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #reconcile = () => {};

  #pendingAdd = false;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener('variant:change', this.#handleCustomVariantChange, { signal });

    this.#getInitialQuantity();
    this.#syncRequiredOptions();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#overlayObserver?.disconnect();
    delete document.body.dataset.ibStickyVisible;
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    let framePending = false;
    this.#reconcile = () => {
      if (framePending) return;
      framePending = true;
      requestAnimationFrame(() => {
        framePending = false;
        if (!this.isConnected) return;
        const passedButtons = buyButtonsBlock.getBoundingClientRect().bottom <= 0;
        const overlayOpen = [...document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"], #shopify-pc__prefs')]
          .some(el => !el.hidden && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
        if (passedButtons && !overlayOpen) this.#showStickyBar();
        else this.#hideStickyBar();
      });
    };
    const { signal } = this.#abortController;
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(this.#reconcile);
    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    // Reconcile geometry for fast scrolling, returning from modals and responsive layouts.
    window.addEventListener('scroll', this.#reconcile, { passive: true, signal });
    window.addEventListener('resize', this.#reconcile, { passive: true, signal });
    for (const event of ['dialog:open', 'dialog:close', 'toggle', 'close']) {
      document.addEventListener(event, this.#reconcile, { capture: true, signal });
    }
    this.#overlayObserver = new MutationObserver(records => {
      if (records.some(record => record.target instanceof Element && record.target.matches('dialog, [role="dialog"], [id^="shopify-pc"]'))) this.#reconcile();
    });
    this.#overlayObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open', 'hidden', 'aria-modal', 'style', 'class'] });
    this.#reconcile();
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    const options = this.closest('.shopify-section')?.querySelector('ib-product-options');
    if (options && !options.ready) {
      options.chooseSize();
      return;
    }
    if (this.#pendingAdd || this.#targetAddToCartButton.disabled) return;
    this.#pendingAdd = true;
    this.#syncRequiredOptions();
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (String(event.detail.data.productId) !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
    this.#syncRequiredOptions();
  };

  #handleCustomVariantChange = (event) => {
    if (String(event.detail?.productId) !== this.dataset.productId) return;
    const variant = event.detail?.variant;
    if (variant) {
      const title = this.querySelector('.sticky-add-to-cart__variant');
      const selectedColor = this.closest('.shopify-section')?.querySelector('[data-color][aria-checked="true"]');
      if (title) { title.textContent = [event.detail.color, event.detail.size].filter(Boolean).join(' / '); title.style.display = ''; }
      this.dataset.currentVariantId = String(variant.id);
      if (event.detail.media && this.refs.productImage) {
        this.refs.productImage.hidden = false;
        const imageUrl = new URL(event.detail.media, location.origin);
        imageUrl.searchParams.set('width', '120');
        this.refs.productImage.src = imageUrl.href;
        this.refs.productImage.srcset = '';
      }
    }
    this.#syncRequiredOptions();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
    this.#pendingAdd = false;
    this.#syncRequiredOptions();
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    document.body.dataset.ibStickyVisible = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    delete document.body.dataset.ibStickyVisible;
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  #syncRequiredOptions() {
    const section = this.closest('.shopify-section');
    const options = section?.querySelector('ib-product-options');
    const needsSize = Boolean(options && !options.ready);
    const blocked = this.#pendingAdd || (!needsSize && Boolean(this.#targetAddToCartButton?.disabled));
    this.refs.addToCartButton.disabled = blocked;
    this.refs.addToCartButton.setAttribute('aria-disabled', String(blocked));
    const text = needsSize ? this.dataset.chooseSizeLabel : this.dataset.addLabel;
    const label = this.querySelector('.add-to-cart-text__content > span:first-child');
    if (label && text) label.textContent = text;
    if (text) this.refs.addToCartButton.setAttribute('aria-label', text);
    this.refs.addToCartButton.setAttribute('aria-busy', String(this.#pendingAdd));
    this.dataset.needsSize = String(needsSize);
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
